import React, { useState } from "react";
import { Copy, Check, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { MODES } from "@/lib/constants";
import { usePickAddress, useTxNav } from "@/lib/navContext";
import { cn, truncMid } from "@/lib/format";

// ---------- UI atoms shared across summary panels, tables and widgets ----------

export const AddressLink = ({ value, display, className }) => {
  const pickAddress = usePickAddress();
  const text = display || value;
  const isAddr = typeof value === "string" && /^addr(_test)?1[a-z0-9]+$/.test(value);
  if (!pickAddress || !value || !isAddr) {
    return <span className={className}>{text}</span>;
  }
  return (
    <button
      type="button"
      data-testid="address-link"
      onClick={() => pickAddress(value)}
      title="Open address"
      className={cn(className, "text-left break-all hover:underline decoration-dotted underline-offset-2 cursor-pointer")}
    >
      {text}
    </button>
  );
};

export const CopyBtn = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid={label ? `copy-${label}` : "copy-btn"}
      aria-label="Copy"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value || "");
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {}
      }}
      className="inline-flex items-center justify-center w-5 h-5 text-muted-foreground hover:text-foreground transition-colors"
    >
      {copied ? <Check size={12} weight="bold" /> : <Copy size={12} />}
    </button>
  );
};

// Copies the current page URL (a deep-link to the exact view) for sharing.
export const ShareLinkButton = () => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid="copy-link-btn"
      title="Copy a shareable link to this view"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(window.location.href);
          setCopied(true);
          toast.success("Link copied", { description: "Shareable link to this view is on your clipboard." });
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error("Couldn't copy link");
        }
      }}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.15em] mono transition-colors",
        copied
          ? "border-[#22d3ee]/60 text-[#22d3ee] bg-[#22d3ee]/[0.06]"
          : "border-primary/25 bg-primary/[0.04] text-muted-foreground hover:text-foreground hover:border-[#22d3ee]/50",
      )}
    >
      {copied ? <Check size={12} weight="bold" /> : <LinkIcon size={12} />}
      {copied ? "Link copied" : "Copy link"}
    </button>
  );
};

// Small inline "copy shareable link" action for individual rows (block / tx /
// pool). Builds a deep-link to that item's view without opening it first.
export const RowShareBtn = ({ modeKey, id, className }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid="row-share-btn"
      aria-label="Copy shareable link to this item"
      title="Copy shareable link"
      onClick={async (e) => {
        e.stopPropagation();
        if (!id) return;
        const tab = MODES[modeKey]?.defaultTab;
        const url = `${window.location.origin}/${modeKey}/${encodeURIComponent(id)}${tab ? `?tab=${tab}` : ""}`;
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          toast.success("Link copied", { description: "Shareable link to this item is on your clipboard." });
          setTimeout(() => setCopied(false), 1400);
        } catch {
          toast.error("Couldn't copy link");
        }
      }}
      className={cn(
        "inline-flex items-center justify-center w-5 h-5 text-muted-foreground hover:text-[#22d3ee] transition-colors",
        className,
      )}
    >
      {copied ? <Check size={12} weight="bold" /> : <LinkIcon size={12} />}
    </button>
  );
};

export const TxHashCell = ({ value, len = 10 }) => {
  const onPickTx = useTxNav();
  if (!value) return <span className="text-muted-foreground">—</span>;
  if (!onPickTx) return <HashCell value={value} len={len} />;
  return (
    <span className="inline-flex items-center gap-1.5 mono text-foreground">
      <button
        type="button"
        data-testid="tx-hash-link"
        onClick={() => onPickTx(value)}
        className="text-foreground underline decoration-dotted decoration-border underline-offset-2 hover:text-primary hover:decoration-primary transition-colors"
        title="Open in transactions explorer"
      >
        {truncMid(value, len, Math.max(4, Math.floor(len / 2)))}
      </button>
      <CopyBtn value={value} label="hash" />
    </span>
  );
};

export const Mono = ({ children, className }) => (
  <span className={cn("mono text-[13px] tabnum", className)}>{children}</span>
);

export const HashCell = ({ value, len = 10 }) => (
  <span className="inline-flex items-center gap-2 group">
    <Mono className="text-foreground/90">{truncMid(value, len, 6)}</Mono>
    <CopyBtn value={value} label="hash" />
  </span>
);

export const AddressCell = ({ value, len = 10 }) => (
  <span className="inline-flex items-center gap-2 group">
    <AddressLink value={value} display={truncMid(value, len, 6)} className="mono text-[13px] tabnum text-foreground/90" />
    <CopyBtn value={value} label="address" />
  </span>
);

export const Badge = ({ children, tone = "default" }) => {
  const tones = {
    default: "border-border text-muted-foreground",
    success: "border-emerald-500/40 text-emerald-400 bg-emerald-500/5",
    warn: "border-yellow-500/40 text-yellow-400 bg-yellow-500/5",
    info: "border-blue-500/40 text-blue-400 bg-blue-500/5",
    danger: "border-red-500/40 text-red-400 bg-red-500/5",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 text-[10px] tracking-widest uppercase border",
        tones[tone] || tones.default,
      )}
    >
      {children}
    </span>
  );
};

// ---------- summary tiles ----------
export const Tile = ({ label, value, hint, testid }) => (
  <div
    className="border border-primary/15 bg-card/60 p-5 flex flex-col gap-2.5 rounded-lg"
    data-testid={testid}
  >
    <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{label}</div>
    <div className="text-[15px] mono tabnum text-foreground break-all">{value}</div>
    {hint ? (
      <div className="text-[10px] mono text-muted-foreground/80">{hint}</div>
    ) : null}
  </div>
);

// ---------- Cardanoscan-style label/value row (used by Address + Transaction summary panels) ----------
export const TxDetailRow = ({ label, children, testid }) => (
  <div className="flex items-start justify-between gap-6 py-3 border-b border-primary/10 last:border-b-0">
    <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono shrink-0 pt-0.5">
      {label}
    </span>
    <span data-testid={testid} className="mono text-[13px] tabnum text-right break-all min-w-0">
      {children}
    </span>
  </div>
);
